import { useState } from 'react';
import { ReactSelectMenu } from '../../Dropdown/StyledReactSelect';
import { components, MenuProps, OptionProps } from 'react-select';
import { IconName } from '../../Icon';
import * as ReportAlertSelectMenuUI from './ReportAlertSelectMenuUI';
import FieldError from '../../Form/FieldError';
import { EmptyComponent } from '../../Dropdown/StyledReactSelect/SelectUtils';

export interface IReportItem {
  value: string;
  label: string;
  createdAt: string;
  icon: IconName;
}

interface IProps {
  selectedReports: IReportItem[];
  allReports: IReportItem[];
  setSelectedReports: (reports: IReportItem[]) => void;
}

export default ({
  selectedReports,
  setSelectedReports,
  allReports,
}: IProps) => {
  const [hasInteracted, setHasInteracted] = useState(false);

  const SelectReportsOption = (props: OptionProps<IReportItem, true>) => {
    const { data, isSelected } = props;

    const handleToggle = () => {
      const updatedSelection = isSelected
        ? selectedReports.filter((report) => report.value !== data.value)
        : [...selectedReports, data];

      setSelectedReports(updatedSelection);
      setHasInteracted(true);
    };

    return (
      <ReportAlertSelectMenuUI.OptionContainer
        $isSelected={isSelected}
        onClick={handleToggle}
      >
        <ReportAlertSelectMenuUI.OptionContent>
          <ReportAlertSelectMenuUI.OptionCheckbox checked={isSelected} />
          <ReportAlertSelectMenuUI.OptionData>
            <ReportAlertSelectMenuUI.OptionIcon name={data.icon} />
            <ReportAlertSelectMenuUI.OptionLabelContainer>
              <ReportAlertSelectMenuUI.OptionLabel>
                {data.label}
              </ReportAlertSelectMenuUI.OptionLabel>
              <ReportAlertSelectMenuUI.OptionDate>
                {data.createdAt}
              </ReportAlertSelectMenuUI.OptionDate>
            </ReportAlertSelectMenuUI.OptionLabelContainer>
          </ReportAlertSelectMenuUI.OptionData>
        </ReportAlertSelectMenuUI.OptionContent>
      </ReportAlertSelectMenuUI.OptionContainer>
    );
  };

  const SelectReportsMenu = (props: MenuProps<IReportItem, true>) => {
    const { children } = props;

    const allSelected = allReports.length === selectedReports.length;

    const handleSelectAll = () => {
      const updatedSelection = allSelected ? [] : allReports;
      setSelectedReports(updatedSelection);
      setHasInteracted(true);
    };

    return (
      <components.Menu {...props}>
        {allReports.length > 0 && (
          <ReportAlertSelectMenuUI.MenuContainer onClick={handleSelectAll}>
            <ReportAlertSelectMenuUI.MenuSelectAllInput checked={allSelected} />
            Select all
          </ReportAlertSelectMenuUI.MenuContainer>
        )}
        {children}
      </components.Menu>
    );
  };

  const SelectReportsIndicatorsContainer = () => {
    return (
      <ReportAlertSelectMenuUI.IndicatorsContainer>
        {selectedReports.length > 0 && `${selectedReports.length} Selected`}
      </ReportAlertSelectMenuUI.IndicatorsContainer>
    );
  };

  const error =
    hasInteracted && selectedReports.length === 0
      ? 'Please select at least 1 report'
      : '';

  return (
    <ReportAlertSelectMenuUI.Container>
      <ReportAlertSelectMenuUI.Label>
        Select reports to be alerted on
      </ReportAlertSelectMenuUI.Label>
      <ReactSelectMenu<IReportItem, true>
        isMulti
        options={allReports}
        value={selectedReports}
        onChange={(newValue) => setSelectedReports([...newValue])}
        placeholder="Select reports"
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isSearchable={false}
        components={{
          Option: SelectReportsOption,
          Menu: SelectReportsMenu,
          MultiValue: EmptyComponent,
          IndicatorsContainer: SelectReportsIndicatorsContainer,
        }}
        isClearable
        menuPlacement="auto"
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
      {error && <FieldError type="custom" customError={error} />}
    </ReportAlertSelectMenuUI.Container>
  );
};
